import { useState, useRef, useEffect } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useSetAtom } from "jotai"
import { ArrowLeft, FileText, PanelLeftClose, PanelLeftOpen } from "lucide-react"
import { Button, Skeleton, toast } from "@aix/ui"
import { api } from "~/utils/api"
import { useAuthSession } from "~/hooks/useAuthSessionVite"
import { AssetViewer } from "~/components/MediaSpace/AssetViewer"
import { CommentPanel } from "~/components/MediaSpace/CommentPanel"
import { CommentInput } from "~/components/MediaSpace/CommentInput"
import { TranscriptionPanel } from "~/components/MediaSpace/TranscriptionPanel"
import { hideFolderSidebarAtom } from "~/context/mediaSpaceAtoms"
import type { PlayerHandle } from "~/components/MediaSpace/VideoPlayer"
import type { IAsset } from "@aix/vidmax-shared/src/constants/prismaTypings"

function AssetDetailPage() {
  const { assetId } = Route.useParams()
  const navigate = useNavigate()
  const { session } = useAuthSession()
  const setHideSidebar = useSetAtom(hideFolderSidebarAtom)

  const playerRef = useRef<PlayerHandle>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [spatialMode, setSpatialMode] = useState(false)
  const [spatialPosition, setSpatialPosition] = useState<{ x: number; y: number } | null>(null)
  const [showTranscript, setShowTranscript] = useState(true)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editName, setEditName] = useState("")

  // Hide folder sidebar on mount, restore on unmount
  useEffect(() => {
    setHideSidebar(true)
    return () => setHideSidebar(false)
  }, [setHideSidebar])

  const utils = api.useUtils()
  const { data: assetData, isLoading, error } = api.mediaSpace.getAssetDetail.useQuery({ assetId })

  const renameMutation = api.mediaSpace.updateAssetName.useMutation({
    onSuccess: () => {
      setIsEditingName(false)
      void utils.mediaSpace.getAssetDetail.invalidate({ assetId })
      void utils.mediaSpace.getFolderContents.invalidate()
    },
    onError: (err) => toast.error(err.message),
  })

  const handleSaveName = () => {
    if (!editName.trim()) return
    renameMutation.mutate({ assetId, name: editName.trim() })
  }

  if (isLoading) {
    return (
      <div className="flex h-full">
        <div className="flex-1 p-4">
          <Skeleton className="w-full h-[60vh] rounded-lg" />
        </div>
        <div className="w-80 border-l p-3 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !assetData) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">Asset not found</h3>
          <Button variant="outline" onClick={() => navigate({ to: "/media-space" })}>
            Back to Media Space
          </Button>
        </div>
      </div>
    )
  }

  const asset = assetData as unknown as IAsset & {
    comments?: {
      id: string
      body: string
      timestampSec?: number | null
      positionX?: number | null
      positionY?: number | null
      resolved: boolean
      createdBy?: { id: string; name?: string | null }
    }[]
  }

  const isVideoOrAudio = asset.assetType === "video" || asset.assetType === "audio"
  const existingTranscript = ((asset as unknown as { misc?: Record<string, unknown> }).misc?.transcript ??
    null) as React.ComponentProps<typeof TranscriptionPanel>["transcript"]

  const spatialComments = (asset.comments ?? [])
    .filter((c) => c.positionX != null && c.positionY != null)
    .map((c) => ({
      id: c.id,
      positionX: c.positionX!,
      positionY: c.positionY!,
      timestampSec: c.timestampSec,
      body: c.body,
      createdBy: c.createdBy,
    }))

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: "calc(100vh - 56px)" }}>
      {/* Compact header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate({ to: "/media-space" })}>
          <ArrowLeft size={16} />
        </Button>

        {/* Transcript toggle (only for video/audio) */}
        {isVideoOrAudio && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setShowTranscript(!showTranscript)}
          >
            {showTranscript ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
          </Button>
        )}

        <div className="flex-1 min-w-0">
          {isEditingName ? (
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveName()
                if (e.key === "Escape") setIsEditingName(false)
              }}
              className="text-sm font-semibold bg-transparent border-b border-primary outline-hidden w-full"
            />
          ) : (
            <button
              type="button"
              className="text-sm font-semibold truncate cursor-pointer hover:text-primary transition-colors text-left bg-transparent border-none p-0"
              onClick={() => {
                setEditName(asset.name || "")
                setIsEditingName(true)
              }}
              title="Click to rename"
            >
              {asset.name || "Untitled"}
            </button>
          )}
        </div>

        <span className="text-[10px] text-muted-foreground uppercase flex items-center gap-1 shrink-0">
          <FileText size={10} />
          {asset.assetType}
        </span>
      </div>

      {/* Three-panel layout — each panel has its own scroll */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Left: Transcription panel (collapsible, own scroll) */}
        {showTranscript && isVideoOrAudio && (
          <div className="w-72 border-r flex-shrink-0 overflow-y-auto">
            <TranscriptionPanel
              assetId={assetId}
              assetUrl={asset.uploadedUrl}
              currentTime={currentTime}
              transcript={existingTranscript}
              onSeek={(sec) => playerRef.current?.seekTo(sec)}
            />
          </div>
        )}

        {/* Center: Asset viewer — fills space, never scrolls */}
        <div className="flex-1 flex items-center justify-center min-w-0 bg-black overflow-hidden">
          <AssetViewer
            ref={playerRef}
            asset={asset}
            comments={spatialComments}
            currentTime={currentTime}
            spatialMode={spatialMode}
            pendingSpatialPosition={spatialPosition}
            onTimeUpdate={setCurrentTime}
            onClickPosition={(x, y) => setSpatialPosition({ x, y })}
            onMarkerClick={(commentId) => {
              document.getElementById(`comment-${commentId}`)?.scrollIntoView({ behavior: "smooth" })
            }}
          />
        </div>

        {/* Right: Comments panel (own scroll) */}
        <div className="w-80 border-l flex flex-col shrink-0 overflow-hidden">
          <div className="p-3 border-b shrink-0">
            <h3 className="text-sm font-semibold">Comments</h3>
          </div>

          <CommentPanel
            assetId={assetId}
            currentUserId={session?.user?.id}
            onSeek={(sec) => playerRef.current?.seekTo(sec)}
          />

          <CommentInput
            assetId={assetId}
            currentTimeSec={isVideoOrAudio ? currentTime : undefined}
            onSpatialModeChange={setSpatialMode}
            spatialPosition={spatialPosition}
            onClearSpatialPosition={() => setSpatialPosition(null)}
          />
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute("/media-space/asset/$assetId")({
  component: AssetDetailPage,
})
